import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionTitle from "@/components/base/SectionTitle";
import WorkCard from "@/components/feature/WorkCard";
import { NewsFeaturedCard, NewsRow } from "@/components/feature/NewsCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { movies, tvShows, works } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: `${works.length}`, label: "收录作品", suffix: "部" },
  { value: `${newsList.length}`, label: "资讯条目", suffix: "篇" },
  { value: "2015 — 2026", label: "作品时间跨度", suffix: "" },
  { value: "6", label: "4K 修复版本", suffix: "部" },
];

const hotTags = ["家庭叙事", "治愈系", "悬疑剧集", "海边取景", "都市群像", "4K 修复", "幕后花絮"];

export default function Home() {
  usePageMeta(
    "大桥未久影视作品大全及最新资讯网站",
    "大桥未久官网聚合影视作品信息，涵盖电影电视剧介绍与资讯，方便用户了解大桥未久相关影视动态和作品详情。",
    "大桥未久,影视作品,电影介绍,电视剧,演员资料,影视资讯,作品动态",
  );

  const featuredMovies = [...movies].sort((a, b) => b.year - a.year).slice(0, 4);
  const featuredTv = [...tvShows].sort((a, b) => b.year - a.year).slice(0, 4);
  const latestNews = newsList.slice(0, 5);
  const [featuredNews, ...restNews] = latestNews;

  return (
    <SiteLayout>
      <HeroCarousel />

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-2 gap-6 px-4 py-7 md:grid-cols-4 md:px-6 md:py-9">
          {stats.map((item) => (
            <div key={item.label} className="min-w-0">
              <p className="font-label text-lg font-semibold text-foreground-950 md:text-2xl">
                {item.value}
                {item.suffix && (
                  <span className="ml-1 text-xs font-normal text-foreground-400">
                    {item.suffix}
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="Movie selection"
            title="电影精选"
            description="从早期文艺片到近年的修复重映，按上映时间整理的院线电影作品。"
            action={
              <Link
                to="/category/movie"
                className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                查看全部电影
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {featuredMovies.map((work, index) => (
              <WorkCard key={work.id} work={work} delay={index * 60} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100/70">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="Series selection"
            title="电视剧精选"
            description="长篇幅的故事更适合慢慢看，这里收录了近年口碑较好的剧集作品。"
            action={
              <Link
                to="/category/tv"
                className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                查看全部剧集
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {featuredTv.map((work, index) => (
              <WorkCard key={work.id} work={work} delay={index * 60} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="Latest news"
            title="最新资讯"
            description="作品动态、行业观察与幕后记录，每周一、周四更新。"
            action={
              <Link
                to="/news"
                className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                进入资讯中心
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr]">
            <NewsFeaturedCard item={featuredNews} />
            <div className="flex flex-col justify-center rounded-lg border border-background-200 bg-background-50 px-5">
              {restNews.map((item) => (
                <NewsRow key={item.id} item={item} showCover={false} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-primary-50">
        <div className="mx-auto grid w-full max-w-[1280px] items-center gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2">
          <div className="min-w-0">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-700">
              About the archive
            </p>
            <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              一个专注作品资料的影视内容站
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              「大桥未久影视内容站」以作品档案为核心，把电影、电视剧的上映信息、
              剧情简介与演职员资料整理成清晰可检索的条目，同时收录相关行业资讯。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站为演示站点，全部作品与资讯内容均为虚构示例，不提供播放、下载与付费服务。
              搜索、筛选与收藏功能均在浏览器本地完成。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {hotTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full border border-primary-200 bg-background-50 px-3 py-1.5 text-xs text-primary-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                >
                  {tag}
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-7 inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解本站定位
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="relative w-full overflow-hidden rounded-lg bg-primary-100">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20film%20archive%20room%20with%20shelves%20of%20film%20canisters%20in%20warm%20amber%20light%2C%20deep%20navy%20shadows%2C%20editorial%20photography%2C%20no%20people&width=800&height=600&seq=about-archive&orientation=landscape"
              alt="影视资料档案室"
              title="大桥未久影视作品档案"
              className="h-[260px] w-full object-cover object-top md:h-[340px]"
            />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}